import { API_URL } from '../config/api';

/**
 * User Initialization Service
 * Handles creating default subscription and usage limits for new users
 */

export interface UserInitResult {
  success: boolean;
  subscription?: any;
  error?: string;
  message?: string;
}

class UserInitService {
  /**
   * Initialize a new user with free tier subscription
   * Call this after Clerk signup completes
   */
  async initializeNewUser(userId: string, email?: string): Promise<UserInitResult> {
    try {
      console.log(`🆕 Initializing new user: ${userId}`);

      const response = await fetch(`${API_URL}/api/user/initialize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          email,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        console.error('❌ Error initializing user:', data);
        return {
          success: false,
          error: data.error || 'Failed to initialize user',
        };
      }

      console.log('✅ User initialized successfully:', data);
      return {
        success: true,
        subscription: data.subscription,
        message: data.message,
      };
    } catch (error: any) {
      console.error('❌ Exception initializing user:', error);
      return {
        success: false,
        error: error.message || 'Failed to initialize user',
      };
    }
  }

  /**
   * Get current user's subscription status
   */
  async getUserSubscription(userId: string): Promise<any> {
    try {
      const response = await fetch(`${API_URL}/api/user/subscription/${userId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        console.error('❌ Error fetching subscription:', data);
        return null;
      }

      return data.subscription;
    } catch (error) {
      console.error('❌ Exception fetching subscription:', error);
      return null;
    }
  }

  /**
   * Check if user needs initialization
   * Returns true if user has no subscription record
   */
  async needsInitialization(userId: string): Promise<boolean> {
    try {
      const subscription = await this.getUserSubscription(userId);
      return subscription === null;
    } catch (error) {
      console.error('❌ Error checking initialization status:', error);
      return false;
    }
  }
}

export const userInitService = new UserInitService();

